import { z } from "zod";

import {
  MULTI_LEG_ORDER_TYPES,
  MULTI_LEG_TIME_IN_FORCE,
} from "~/v2/constants/multiLegOrder";
import { POSITION_INTENT } from "~/v2/constants/optionOrder";
import { isGTZero } from "../shared/utils";

export const zMultiLegOrderSchema = z.object({
  orderType: z.enum(MULTI_LEG_ORDER_TYPES),
  timeInForce: z.enum(MULTI_LEG_TIME_IN_FORCE),
  quantity: z.coerce.number().int("Quantity must be a whole number"),
  limitPrice: z.coerce.number().optional(),
  legs: z
    .object({
      symbol: z.string(),
      ratioQty: z.coerce.number().int("Ratio quantity must be a whole number"),
      positionIntent: z.enum(POSITION_INTENT),
    })
    .array(),
});

export type MultiLegOrderSchema = z.infer<typeof zMultiLegOrderSchema>;

export const multiLegSchemaSuperRefinement = (
  { orderType, quantity, limitPrice, legs }: MultiLegOrderSchema,
  ctx: z.RefinementCtx,
) => {
  if (!Number.isInteger(quantity)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Quantity must be a whole number",
      path: ["quantity"],
    });
  }

  if (!isGTZero(quantity)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Quantity must be greater than 0",
      path: ["quantity"],
    });
  }

  if (
    orderType === "limit" &&
    (limitPrice === undefined || Number.isNaN(limitPrice))
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Limit Price is required for limit orders",
      path: ["limitPrice"],
    });
  }

  if (legs.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "At least one leg is required",
      path: ["legs"],
    });
  }

  legs.forEach((leg, i) => {
    if (!isGTZero(leg.ratioQty)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Ratio quantity must be greater than 0",
        path: ["legs", i, "ratioQty"],
      });
    }
  });
};
